import { List, Map } from "immutable";
import { isObject } from "lodash";

import { GLOBAL_FILTER_ID, ORIENTATION_ROW, TIMELINE_VIEW } from "../../constants/identifiers";
import itemTypeCategories from "../../constants/itemTypeCategories";
import { PropertyFormatIdentifier } from "../../constants/propertyFormats";
import attachmentSelectors from "../../modules/attachments/selectors";
import dateFunctions, { isDateValid } from "../../modules/definition/calendar/dateFunctions";
import dateByAddingSpan from "../../modules/definition/calendar/dateFunctions/dateByAddingSpan";
import { iso8601StringFromTimestamp } from "../../modules/definition/calendar/dateFunctions/dateConversion";
import dateWithTimestamp from "../../modules/definition/calendar/dateFunctions/dateWithTimestamp";
import {
  getDaysSinceZeroDate,
  getHoursSinceZeroDate,
  getWeeksSinceZeroDate
} from "../../modules/definition/calendar/dateFunctions/stringFromZeroDate";
import { immutableRecords } from "../../types";
import calendarTypes from "../../types/calendarTypes";
import { ViewDisplayRecord } from "../../types/immutableRecords/display/viewLayout/ViewDisplayRecord";
import { ViewLayoutRecord } from "../../types/immutableRecords/model/appState/ViewLayoutRecord";
import { FilterRecord } from "../../types/immutableRecords/model/data/FilterRecord";

const flattenViewChildren = layout => {
  let newLayout = layout;
  for (let i = 0; i < newLayout.children.size; i++) {
    if (typeof newLayout.children.get(i) != "string") {
      const added = newLayout.children.concat(newLayout.children.get(i).children);
      newLayout = newLayout.set("children", added);
      const deleted = newLayout.children.delete(i);
      newLayout = newLayout.set("children", deleted);
    }
  }
  return newLayout;
};

const removeView = (layout, viewId) => {
  let newLayout = layout;
  let i = newLayout.children.size;
  while (i--) {
    if (typeof newLayout.children.get(i) === "string") {
      if (newLayout.children.get(i) === viewId) {
        const deleted = newLayout.children.delete(i);
        newLayout = newLayout.set("children", deleted);
      }
    }
  }
  return newLayout;
};

const v3ToStandardConverter = (
  v3Data,
  attachmentPath,
  disableFiltering,
  disableFilteringMore,
  disableSplitView,
  disableTimelineZoom,
  skin
) => {
  const calendar = v3Data.definitions.calendar;

  // Definition > Types
  const types = v3Data.definitions.types.byId.reduce((list, type) => {
    let properties = List.isList(type.properties) ? type.properties : List();
    properties = properties.concat(["color", "image", "links", "attachments", "summary", "tags", "initials"]);

    return list.push(
      immutableRecords.StandardTypeRecord({
        id: type.id, // Required
        label: type.label, // Required
        pluralLabel: type.pluralLabel, // Required
        hasDates: type.canHaveDate, // Optional
        superTypes: List.isList(type.superTypes) ? type.superTypes : List(), // Optional
        properties: properties, // Optional
        relationshipTypes: List.isList(type.references) ? type.references : List(), // Optional
        isLocation: type.category === itemTypeCategories.LOCATION // Optional
      })
    );
  }, List());

  // Definition > Properties
  let properties = v3Data.definitions.properties.byId.reduce((list, property) => {
    let format = "string";
    switch (property.format) {
      case PropertyFormatIdentifier.text:
      case PropertyFormatIdentifier.multitext:
      case PropertyFormatIdentifier.list:
        format = "string";
        break;
      case PropertyFormatIdentifier.percentage:
      case PropertyFormatIdentifier.integer:
      case PropertyFormatIdentifier.double:
      case PropertyFormatIdentifier.currency:
        format = "number";
        break;
      case PropertyFormatIdentifier.boolean:
        format = "boolean";
        break;
    }

    return list.push(
      immutableRecords.StandardPropertyRecord({
        id: property.id, // Required
        label: property.label, // Required
        dataFormat: format, // Required, “string”, “number”, or “boolean”
        allowedValues: List.isList(property.allowed)
          ? property.allowed.map(opt => (isObject(opt) ? opt.label : opt))
          : null, // Optional
        min: null, // Optional
        max: null, // Optional
        multiple: property.format === "boolean" ? false : property.multiple // Optional, must be false if format = boolean
      })
    );
  }, List());

  // Add properties for colours, images, links and attachments
  const colors = immutableRecords.StandardPropertyRecord({
    id: "color",
    label: "Colors",
    dataFormat: "string",
    allowedValues: v3Data.definitions.colors.byId.size
      ? v3Data.definitions.colors.byId.map(color => color.swatchColor).toList()
      : List(),
    multiple: false
  });

  const image = immutableRecords.StandardPropertyRecord({
    id: "image",
    label: "Image",
    dataFormat: "string",
    multiple: false
  });
  const links = immutableRecords.StandardPropertyRecord({
    id: "links",
    label: "Links",
    dataFormat: "string",
    multiple: true
  });
  const attachments = immutableRecords.StandardPropertyRecord({
    id: "attachments",
    label: "Attachments",
    dataFormat: "string",
    multiple: true
  });
  const summary = immutableRecords.StandardPropertyRecord({
    id: "summary",
    label: "Summary",
    dataFormat: "string",
    multiple: false
  });
  const tags = immutableRecords.StandardPropertyRecord({
    id: "tags",
    label: "Tags",
    dataFormat: "string",
    multiple: true
  });
  const initials = immutableRecords.StandardPropertyRecord({
    id: "initials",
    label: "Initials",
    dataFormat: "string",
    multiple: false
  });
  properties = properties.concat([colors, image, links, attachments, summary, tags, initials]);

  // Definition > Relationship Types
  const relationshipTypes = v3Data.definitions.references.byId.reduce((list, relationshipType) => {
    return list.push(
      immutableRecords.StandardRelationshipTypeRecord({
        id: relationshipType.id, // Required
        label: relationshipType.label, // Required
        types: List.isList(relationshipType.types) ? relationshipType.types : List(), // Required
        multiple: true // Optional, bool
      })
    );
  }, List());

  // Definition > isRelative and relativeUnits
  let isRelative = true;
  let relativeUnits = null;

  switch (v3Data.definitions.calendar.calendarType) {
    case calendarTypes.CALENDAR_TYPE_DATE_TIME:
      isRelative = false;
      break;
    case calendarTypes.CALENDAR_TYPE_WEEKDAY_TIME:
      relativeUnits = "weeks";
      break;
    case calendarTypes.CALENDAR_TYPE_DAY_TIME:
      relativeUnits = "days";
      break;
    case calendarTypes.CALENDAR_TYPE_HOUR_TIME:
      relativeUnits = "hours";
      break;
  }

  // Definition
  const definition = immutableRecords.StandardDefinitionRecord({
    types: types, // Required
    properties: properties, // Optional
    relationshipTypes: relationshipTypes, // Optional
    isRelative: isRelative, // Optional
    relativeUnits: relativeUnits, // Required if isRelative
    timescale: "UTC" // Optional
  });

  const convertDate = (dateForRelative, dateForAbsolute) => {
    let result = null;
    if (isDateValid(dateForRelative)) {
      if (isRelative) {
        switch (v3Data.definitions.calendar.calendarType) {
          case calendarTypes.CALENDAR_TYPE_WEEKDAY_TIME:
            result = getWeeksSinceZeroDate(calendar, dateForRelative);
            break;
          case calendarTypes.CALENDAR_TYPE_DAY_TIME:
            result = getDaysSinceZeroDate(calendar, dateForRelative);
            break;
          case calendarTypes.CALENDAR_TYPE_HOUR_TIME:
            result = getHoursSinceZeroDate(calendar, dateForRelative);
            break;
        }
      } else {
        result = iso8601StringFromTimestamp(calendar, dateForAbsolute ? dateForAbsolute : dateForRelative);
      }
    }
    return result;
  };

  // Data > Items
  const items = v3Data.data.items.byId.reduce((list, item) => {
    // Start date
    const start = isDateValid(item.preferredStartDate)
      ? convertDate(dateWithTimestamp(calendar, item.preferredStartDate.timestamp), item.preferredStartDate)
      : null;
    const latestStart = isDateValid(item.latestStartDate)
      ? convertDate(dateWithTimestamp(calendar, item.latestStartDate.timestamp), item.latestStartDate)
      : null;

    // End date
    let end = null;
    let earliestEnd = null;
    if (start) {
      if (item.preferredDuration) {
        end = convertDate(dateByAddingSpan(calendar, item.preferredStartDate, item.preferredDuration));
      }
      if (item.durationToEarliestEnd) {
        earliestEnd = convertDate(dateByAddingSpan(calendar, item.preferredStartDate, item.durationToEarliestEnd));
      }
    }

    // hasDates is used to check if ongoing is allowed
    const hasDates = v3Data.definitions.types.byId.get(item.type).canHaveDate;

    // Add property values for colours, images, links and attachments
    let propertyValues = Map(item.propertyValues);
    item.propertyValues.map((value, key) => {
      const property = v3Data.definitions.properties.byId.get(key);
      if (property.format === PropertyFormatIdentifier.list) {
        const matchingOption = property.allowed.find(option => option.id === value);
        const stringValue = matchingOption ? matchingOption.label : null;
        propertyValues = propertyValues.set(key, stringValue);
      }
    });

    if (item.color) {
      const color = Map(v3Data.definitions.colors.byId).find(
        color => color.id === item.color || color.swatchColor === item.color
      );
      if (color) propertyValues = propertyValues.set("color", color.swatchColor);
    }
    if (item.image) {
      const attachment = v3Data.data.attachments.byId.get(item.image);
      const isAttachmentEmbedded = attachmentSelectors.isAttachmentEmbedded(attachment);
      const attachmentPathWithSlash =
        (attachmentPath ? attachmentPath + (attachmentPath.substr(-1) === "/" ? "" : "/") : "attachments/") +
        attachment.id +
        "/";

      propertyValues = propertyValues.set(
        "image",
        isAttachmentEmbedded ? attachmentPathWithSlash + attachment.label : attachment.path
      );
    }
    if (item.links.size) {
      propertyValues = propertyValues.set(
        "links",
        item.links.map(id => {
          const attachment = v3Data.data.attachments.byId.get(id);
          return attachment.path;
        })
      );
    }
    if (item.attachments.size) {
      propertyValues = propertyValues.set(
        "attachments",
        item.attachments.map(id => {
          const attachment = v3Data.data.attachments.byId.get(id);

          const isAttachmentEmbedded = attachmentSelectors.isAttachmentEmbedded(attachment);
          const attachmentPathWithSlash =
            (attachmentPath ? attachmentPath + (attachmentPath.substr(-1) === "/" ? "" : "/") : "attachments/") +
            attachment.id +
            "/";

          return isAttachmentEmbedded ? attachmentPathWithSlash + attachment.label : attachment.path;
        })
      );
    }
    if (item.summary) {
      propertyValues = propertyValues.set("summary", item.summary);
    }
    if (item.tags) {
      const tags = item.tags.map(tagId => v3Data.data.tags.get(tagId));
      propertyValues = propertyValues.set("tags", tags);
    }
    if (item.initials) {
      propertyValues = propertyValues.set("initials", item.initials);
    }

    return list.push(
      immutableRecords.StandardItemRecord({
        id: item.id, // Required
        type: item.type, // Required
        label: item.label ? item.label : "", // Required
        start: start, // Optional, number or dateString depending on isRelative
        latestStart: latestStart, // Optional, number or dateString depending on isRelative
        earliestEnd: earliestEnd, // Optional, number or dateString depending on isRelative
        end: end, // Optional, number or dateString depending on isRelative
        ongoing: hasDates ? item.ongoing : null, // Optional
        super: item.super, // Optional
        propertyValues: propertyValues, // Optional
        locationUri: item.locationUri // Optional
      })
    );
  }, List());

  // Data > Relationships
  const relationships = v3Data.data.relationships.byId.reduce((list, relationship) => {
    return list.push(
      immutableRecords.StandardRelationshipRecord({
        id: relationship.id, // Required
        subject: relationship.subject, // Required
        relationshipType: relationship.reference, // Required
        target: relationship.object // Required
      })
    );
  }, List());

  // Data
  const data = immutableRecords.StandardDataRecord({
    items: items, // Required
    relationships: relationships // Optional
  });

  // applicableDate
  const today = dateWithTimestamp(calendar, dateFunctions.timestampForToday(calendar));
  const applicableDate = iso8601StringFromTimestamp(calendar, today);

  // App data: open views + filters

  // Remove all views except timeline view
  let openViews = v3Data.appState ? v3Data.appState.desktopOpenViews : null;
  let layout = openViews.layout;
  layout = flattenViewChildren(layout);
  let filters = v3Data.data.filters;
  let timelineViews = Map();
  openViews.byId.map(view => {
    if (view.viewType === TIMELINE_VIEW) {
      // Include global filter in view filter
      filters = filters.setIn(
        ["byId", view.viewId],
        filters.byId.get(GLOBAL_FILTER_ID).mergeDeep(filters.byId.get(view.viewId))
      );
      timelineViews = timelineViews.set(view.viewId, view);
    } else {
      layout = removeView(layout, view.viewId);
      filters = filters.deleteIn(["byId", view.viewId]);
    }
  });
  filters = filters.setIn(["byId", GLOBAL_FILTER_ID], FilterRecord());

  //  Max of 3 views
  if (timelineViews.size > 3) {
    timelineViews = timelineViews.slice(0, 3);
  }

  openViews = openViews.set("byId", timelineViews);

  // If there are no open views left, add a timeline view
  if (!openViews.byId.size) {
    const timelineOptions = v3Data.appState ? v3Data.appState.desktopOpenViews.byId.first().timelineOptions : null;
    openViews = openViews.setIn(
      ["byId", "primaryView"],
      ViewDisplayRecord({
        viewId: "primaryView",
        viewType: TIMELINE_VIEW,
        timelineOptions: timelineOptions
      })
    );
    layout = ViewLayoutRecord({ children: List(["primaryView"]) });
    filters = filters.setIn(["byId", "primaryView"], FilterRecord());
    filters = filters.setIn(["allIds"], ["primaryView"]);
  }
  layout = layout.set("orientation", ORIENTATION_ROW);
  openViews = openViews.set("layout", layout);

  // Make sure the active view exists
  if (!openViews.byId.find(view => view.viewId === openViews.activeView)) {
    openViews = openViews.set("activeView", openViews.byId.first().viewId);
  }

  // App data:  type data
  const typeData = v3Data.definitions.types.byId.reduce((arr, type) => {
    arr.push({
      id: type.id,
      compactDisplay: type.compactDisplay,
      icon: type.icon,
      color: type.color,
      showInSequence: type.showInSequence,
      showOnSidebar: type.showOnSidebar,
      showAsColumn: type.showAsColumn
    });
    return arr;
  }, []);

  // App date: reference data
  const referenceData = v3Data.definitions.references.byId.reduce((arr, ref) => {
    arr.push({
      id: ref.id,
      icon: ref.icon,
      color: ref.color,
      lineStyle: ref.lineStyle,
      bidirectional: ref.bidirectional
    });
    return arr;
  }, []);

  // Timeline
  const timeline = immutableRecords.StandardTimelineRecord({
    definition: definition, // Required
    data: data, // Required
    applicableDate: applicableDate, // Required
    language: null, // TO DO | Optional
    "@context": null, // TO DO | Optional
    appData: {
      aeonTimeline: {
        openViews: openViews,
        scrollingIsLocked: v3Data.appState ? v3Data.appState.scrollingIsLocked : null,
        exposedFilterTypes: v3Data.appState ? v3Data.appState.exposedFilterTypes : null,
        filters: filters,
        types: typeData,
        references: referenceData,
        disableFiltering: disableFiltering,
        disableFilteringMore: disableFilteringMore,
        disableSplitView: disableSplitView,
        disableTimelineZoom: disableTimelineZoom,
        skin: skin,
        timelineViewSettings: v3Data.definitions ? v3Data.definitions.timelineViewSettings : null,
        colors: v3Data.definitions.colors,
        tags: v3Data.data.tags,
        properties: v3Data.definitions.properties
      }
    }
  });

  return timeline.toJS();
};

export default v3ToStandardConverter;
